import { z } from 'zod';

// Schema for contact sub-fields (used in fallbackContact, without nested fallback)
// All fields are optional to support partial saves and full-sync logic
export const contactSubFieldsSchema = z.object({
  name: z.string().optional(),
  phone1: z.string().optional(),
  phone2: z.string().optional(),
  email: z.string().optional(),
});

// Schema for contact field values - matches ContactValue from backend
// All fields are optional to comply with full-sync logic
export const contactValueSchema = z.object({
  name: z.string().optional(),
  phone1: z.string().optional(),
  phone2: z.string().optional(),
  email: z.string().optional(),
  fallbackContact: contactSubFieldsSchema.optional(),
});

export type ContactValue = z.infer<typeof contactValueSchema>;
export type ContactSubFields = z.infer<typeof contactSubFieldsSchema>;

// Schema for a single position entry
// Contains id, label, coordinates, and optional status
// Note: label is optional to allow empty entries during editing
export const positionSchema = z.object({
  id: z.string(),
  label: z.string().optional().default(''),
  latitude: z.string().optional(),
  longitude: z.string().optional(),
  status: z.boolean().optional(),
});

// Schema for positions field values - array of position entries
// Matches PositionsValue from backend
export const positionsValueSchema = z.array(positionSchema);

export type Position = z.infer<typeof positionSchema>;
export type PositionsValue = z.infer<typeof positionsValueSchema>;

// Schema for a single status range entry
// Contains time range and status configuration
export const statusRangeSchema = z.object({
  id: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  statusDuring: z.enum(['red', 'yellow', 'green']),
  fallbackAfter: z.enum(['red', 'yellow', 'green']).optional(),
});

// Schema for status field values - supports manual and scheduled modes
// Matches StatusValue from backend
export const statusValueSchema = z.object({
  mode: z.enum(['manual', 'scheduled']),
  manualStatus: z.enum(['red', 'yellow', 'green']).optional(),
  ranges: z.array(statusRangeSchema).optional(),
  defaultStatus: z.enum(['red', 'yellow', 'green']),
});

// Schema for status option configuration
export const statusOptionSchema = z.object({
  color: z.enum(['red', 'yellow', 'green']),
  labelDe: z.string(),
  labelEn: z.string().optional(),
});

export type StatusColor = 'red' | 'yellow' | 'green';
export type StatusRange = z.infer<typeof statusRangeSchema>;
export type StatusValue = z.infer<typeof statusValueSchema>;
export type StatusOption = z.infer<typeof statusOptionSchema>;

const statusOptionsSchema = z.union([
  z.tuple([statusOptionSchema, statusOptionSchema]),
  z.tuple([statusOptionSchema, statusOptionSchema, statusOptionSchema]),
]);

// Schema for metadata field properties - matches JsonSchemaType from backend
const metadataFieldPropertySchema = z
  .object({
    type: z.enum([
      'string',
      'number',
      'boolean',
      'date',
      'section',
      'contact',
      'positions',
      'status',
    ]),
    format: z.literal('date-time').optional(),
    description: z.string(),
    description_en: z.string().optional(),
    minLength: z.number().optional(),
    customerAccess: z.enum(['none', 'read', 'read-and-write']),
    groupId: z.string().nullable().optional(),
    width: z.enum(['33', '50', '100']).optional(),
    // Positions field status labels
    statusLabel: z.string().optional(),
    statusLabelEn: z.string().optional(),
    // Status field specific properties
    statusOptions: statusOptionsSchema.optional(),
    isGroupIndicator: z.boolean().optional(),
    icon: z.enum(['fuel', 'helideck', 'warning', 'check', 'lightning', 'medical']).optional(),
    // Legacy support for old group field
    group: z.string().nullable().optional(),
  })
  .passthrough();

// Schema for metadata groups
const metadataGroupSchema = z
  .object({
    id: z.string(),
    labelDe: z.string(),
    labelEn: z.string().optional(),
    order: z.number(),
  })
  .passthrough();

// Full JsonSchemaType schema - matches backend types
export const jsonSchemaType = z
  .object({
    type: z.literal('object'),
    groups: z.array(metadataGroupSchema).optional(),
    properties: z.record(z.string(), metadataFieldPropertySchema),
    required: z.array(z.string()),
    propertyOrder: z.array(z.string()),
  })
  .passthrough();
